import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import candidateAuthService from '../../../services/candidateAuthService';

/**
 * CandidateAuthModal.jsx
 * Candidate Authentication Modal
 * Supports:
 * - Email + Password Login
 * - New Candidate Registration + Email 6-digit OTP verification
 * - Email OTP (Passwordless) Login
 * - Forgot Password with OTP Reset
 * - Remember Me persistence
 * - Seamless hand-off to the existing job application form
 */
const CandidateAuthModal = ({ isOpen, onClose, onSuccess, job = null, initialMode = 'login' }) => {
  // Modes: 'login' | 'register' | 'verify_registration_otp' | 'otp_login' | 'verify_login_otp' | 'forgot_password' | 'verify_reset_otp'
  const [mode, setMode] = useState(initialMode);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    otp: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Resend OTP Cooldown (60 seconds)
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimerRef = useRef(null);

  // OTP 6-box input state
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const otpInputRefs = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setError(null);
      setSuccessMessage(null);
      setOtpDigits(['', '', '', '', '', '']);
    }
  }, [isOpen, initialMode]);

  // Cooldown countdown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownTimerRef.current = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, [resendCooldown]);

  const startResendCooldown = (seconds = 60) => {
    setResendCooldown(seconds);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError(null);
    setSuccessMessage(null);
    setOtpDigits(['', '', '', '', '', '']);
  };

  // Handle individual OTP box inputs
  const handleOtpDigitChange = (index, value) => {
    const cleanVal = value.replace(/[^0-9]/g, '');
    if (!cleanVal && value !== '') return;

    const newDigits = [...otpDigits];
    
    // Handle pasting a full 6-digit code
    if (cleanVal.length > 1) {
      const pastedDigits = cleanVal.slice(0, 6).split('');
      pastedDigits.forEach((digit, i) => {
        if (i < 6) newDigits[i] = digit;
      });
      setOtpDigits(newDigits);
      setFormData((prev) => ({ ...prev, otp: newDigits.join('') }));
      const nextIndex = Math.min(pastedDigits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
      return;
    }

    newDigits[index] = cleanVal.slice(-1);
    setOtpDigits(newDigits);
    setFormData((prev) => ({ ...prev, otp: newDigits.join('') }));

    // Auto-advance to next input box
    if (cleanVal && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // 1. Password Login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await candidateAuthService.loginWithPassword({
        email: formData.email,
        password: formData.password,
        rememberMe
      });

      if (result.success) {
        onSuccess(result.data.user);
      } else {
        setError(result.error || 'Invalid email or password.');
      }
    } catch (err) {
      setError('An unexpected error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 2. New Candidate Registration - Step 1: Request Email OTP
  const handleRegisterStep1 = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);

    try {
      const result = await candidateAuthService.requestRegistrationOtp({
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone
      });

      if (result.success) {
        startResendCooldown(60);
        setMode('verify_registration_otp');
        setSuccessMessage(`A 6-digit verification code has been sent to ${formData.email}.`);
      } else {
        setError(result.error || 'Unable to send verification email. Please try again.');
      }
    } catch (err) {
      setError('Unable to send verification email. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // 3. Verify Registration OTP - Step 2: Check 6-digit code
  const handleVerifyRegistrationOtp = async (e) => {
    e.preventDefault();
    const otpCode = otpDigits.join('');

    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit verification code.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await candidateAuthService.verifyRegistrationOtp({
        email: formData.email,
        otp: otpCode
      });

      if (result.success) {
        setFormData((prev) => ({ ...prev, otp: otpCode }));
        setMode('register_password');
        setSuccessMessage('Email verified! Please set your password to complete your account.');
      } else {
        setError(result.error || 'Invalid verification code.');
      }
    } catch (err) {
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 4. Complete Registration - Step 3: Set Password & Create Account
  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    try {
      const result = await candidateAuthService.completeRegistration({
        full_name: formData.name,
        email: formData.email,
        otp: formData.otp || otpDigits.join(''),
        password: formData.password,
        phone: formData.phone,
        rememberMe
      });

      if (result.success) {
        onSuccess(result.data.user);
      } else {
        setError(result.error || 'Failed to complete registration.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 4. Request OTP for Login
  const handleRequestOtpLogin = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await candidateAuthService.requestLoginOtp({
        email: formData.email
      });

      if (result.success) {
        startResendCooldown(60);
        setMode('verify_login_otp');
        setSuccessMessage(`A 6-digit sign-in code has been sent to ${formData.email}.`);
      } else {
        setError(result.error || 'Unable to send login code. Please try again.');
      }
    } catch (err) {
      setError('Unable to send login code. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // 5. Verify Login OTP
  const handleVerifyLoginOtp = async (e) => {
    e.preventDefault();
    const otpCode = otpDigits.join('');

    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit verification code.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await candidateAuthService.verifyLoginOtp({
        email: formData.email,
        otp: otpCode,
        rememberMe
      });

      if (result.success) {
        onSuccess(result.data.user);
      } else {
        setError(result.error || 'Invalid sign-in code.');
      }
    } catch (err) {
      setError('Failed to verify sign-in code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 6. Request Forgot Password OTP
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your registered email address.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await candidateAuthService.forgotPassword({
        email: formData.email
      });

      if (result.success) {
        startResendCooldown(60);
        setMode('verify_reset_otp');
        setSuccessMessage(`Password reset instructions sent to ${formData.email}.`);
      } else {
        setError(result.error || 'Unable to send password reset code. Please try again.');
      }
    } catch (err) {
      setError('Unable to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 7. Verify Reset OTP & Update Password
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otpDigits.join('');

    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit reset code.');
      return;
    }
    if (formData.newPassword !== formData.confirmNewPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await candidateAuthService.resetPassword({
        email: formData.email,
        otp: otpCode,
        new_password: formData.newPassword
      });

      if (result.success) {
        setSuccessMessage('Password reset successfully! Logging you in...');
        const loginResult = await candidateAuthService.loginWithPassword({
          email: formData.email,
          password: formData.newPassword,
          rememberMe
        });

        if (loginResult.success) {
          onSuccess(loginResult.data.user);
        } else {
          switchMode('login');
          setSuccessMessage('Password changed successfully. Please sign in.');
        }
      } else {
        setError(result.error || 'Failed to reset password.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP Action
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || !formData.email) return;

    setError(null);
    setLoading(true);

    const purpose = mode === 'verify_registration_otp' 
      ? 'registration' 
      : mode === 'verify_login_otp' 
      ? 'login' 
      : 'forgot_password';

    try {
      const result = await candidateAuthService.resendOtp({
        email: formData.email,
        purpose
      });

      if (result.success) {
        startResendCooldown(60);
        setSuccessMessage('A fresh 6-digit code has been sent to your email.');
        setOtpDigits(['', '', '', '', '', '']);
      } else {
        setError(result.error || 'Unable to resend verification code. Please try again.');
      }
    } catch (err) {
      setError('Unable to resend code. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
      <div 
        className="max-w-md w-full bg-card rounded-2xl shadow-2xl border border-border p-6 md:p-8 relative my-8 animate-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <Icon name="X" size={20} />
        </button>

        {/* Job Context Badge */}
        {job && (
          <div className="mb-6 p-3 bg-primary/10 border border-primary/20 rounded-xl flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
              <Icon name="Briefcase" size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-primary font-semibold uppercase tracking-wider">Applying For</p>
              <h4 className="text-sm font-bold text-foreground truncate">{job.title}</h4>
              <p className="text-xs text-muted-foreground truncate">{job.company || 'Direct Job Application'}</p>
            </div>
          </div>
        )}

        {/* Header Icon & Titles */}
        <div className="text-center mb-5">
          <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3 text-primary">
            {mode.includes('otp') ? (
              <Icon name="ShieldCheck" size={28} />
            ) : mode === 'register' || mode === 'register_password' ? (
              <Icon name="UserPlus" size={28} />
            ) : mode.includes('forgot') || mode.includes('reset') ? (
              <Icon name="KeyRound" size={28} />
            ) : (
              <Icon name="LogIn" size={28} />
            )}
          </div>

          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {mode === 'login' && 'Candidate Sign In'}
            {mode === 'register' && 'Create Candidate Account'}
            {mode === 'verify_registration_otp' && 'Verify Your Email'}
            {mode === 'register_password' && 'Create Account Password'}
            {mode === 'otp_login' && 'Sign In with Email OTP'}
            {mode === 'verify_login_otp' && 'Enter Verification Code'}
            {mode === 'forgot_password' && 'Reset Password'}
            {mode === 'verify_reset_otp' && 'Set New Password'}
          </h2>

          <p className="text-xs md:text-sm text-muted-foreground mt-1.5">
            {mode === 'login' && 'Sign in to submit your job application'}
            {mode === 'register' && 'Enter your details to receive a 6-digit verification code'}
            {mode === 'verify_registration_otp' && `Enter the 6-digit code sent to ${formData.email}`}
            {mode === 'register_password' && 'Choose a secure password to complete your account setup'}
            {mode === 'otp_login' && 'Receive a one-time login code on your email'}
            {mode === 'verify_login_otp' && `Enter the 6-digit sign-in code sent to ${formData.email}`}
            {mode === 'forgot_password' && "Enter your email to receive a password reset code"}
            {mode === 'verify_reset_otp' && 'Enter the reset code and choose a new password'}
          </p>

          {/* Registration 3-Step Progress Indicator */}
          {(mode === 'register' || mode === 'verify_registration_otp' || mode === 'register_password') && (
            <div className="flex items-center justify-center gap-2 mt-4 px-4">
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${mode === 'register' ? 'text-primary' : 'text-success'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${mode === 'register' ? 'bg-primary text-white' : 'bg-success/20 text-success'}`}>
                  {mode !== 'register' ? '✓' : '1'}
                </span>
                <span>Details</span>
              </div>
              <div className={`w-6 h-0.5 ${mode !== 'register' ? 'bg-success/40' : 'bg-border'}`} />
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${mode === 'verify_registration_otp' ? 'text-primary' : mode === 'register_password' ? 'text-success' : 'text-muted-foreground'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${mode === 'verify_registration_otp' ? 'bg-primary text-white' : mode === 'register_password' ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'}`}>
                  {mode === 'register_password' ? '✓' : '2'}
                </span>
                <span>Verify</span>
              </div>
              <div className={`w-6 h-0.5 ${mode === 'register_password' ? 'bg-primary' : 'bg-border'}`} />
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${mode === 'register_password' ? 'text-primary' : 'text-muted-foreground'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${mode === 'register_password' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  3
                </span>
                <span>Password</span>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-xl text-error text-xs md:text-sm flex items-start gap-2.5">
            <Icon name="AlertCircle" size={18} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{error}</div>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-xl text-success text-xs md:text-sm flex items-start gap-2.5">
            <Icon name="CheckCircle2" size={18} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{successMessage}</div>
          </div>
        )}

        {/* VIEW 1: Password Sign In */}
        {mode === 'login' && (
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Email Address <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground transition-all"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-foreground">
                  Password <span className="text-error">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => switchMode('forgot_password')}
                  className="text-xs text-primary font-medium hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
              <input
                type="password"
                name="password"
                required
                placeholder="••••••••"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground transition-all"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary/50"
                />
                <span className="text-xs text-muted-foreground font-medium">Remember me</span>
              </label>
            </div>

            <Button
              type="submit"
              variant="default"
              className="w-full py-2.5 font-semibold text-sm"
              loading={loading}
            >
              Sign In
            </Button>

            <div className="relative my-4 text-center">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <span className="relative bg-card px-3 text-xs text-muted-foreground uppercase font-medium">
                Or
              </span>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full py-2.5 text-sm flex items-center justify-center gap-2 border-primary/30 text-primary hover:bg-primary/5"
              onClick={() => switchMode('otp_login')}
            >
              <Icon name="Mail" size={16} />
              Continue with Email OTP
            </Button>

            <p className="text-center text-xs text-muted-foreground pt-3">
              New candidate?{' '}
              <button
                type="button"
                onClick={() => switchMode('register')}
                className="text-primary font-semibold hover:underline"
              >
                Create an account
              </button>
            </p>
          </form>
        )}

        {/* VIEW 2: Candidate Registration - Step 1: Full Name & Email */}
        {mode === 'register' && (
          <form onSubmit={handleRegisterStep1} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Full Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                placeholder="e.g. Rahul Sharma"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Email Address <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground transition-all"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                We'll email you a 6-digit verification code before setting up your password.
              </p>
            </div>

            <Button
              type="submit"
              variant="default"
              className="w-full py-2.5 font-semibold text-sm mt-2"
              loading={loading}
            >
              Send Verification Code
            </Button>

            <p className="text-center text-xs text-muted-foreground pt-2">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-primary font-semibold hover:underline"
              >
                Sign In
              </button>
            </p>
          </form>
        )}

        {/* VIEW 3: Candidate Registration - Step 3: Set Password */}
        {mode === 'register_password' && (
          <form onSubmit={handleCompleteRegistration} className="space-y-4">
            <div className="p-3 bg-muted/30 border border-border rounded-xl text-xs space-y-1">
              <div className="flex justify-between text-muted-foreground">
                <span>Candidate:</span>
                <span className="font-semibold text-foreground">{formData.name}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Verified Email:</span>
                <span className="font-semibold text-success flex items-center gap-1">
                  ✓ {formData.email}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Create Password <span className="text-error">*</span>
              </label>
              <input
                type="password"
                name="password"
                required
                placeholder="Minimum 6 characters"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Confirm Password <span className="text-error">*</span>
              </label>
              <input
                type="password"
                name="confirmPassword"
                required
                placeholder="Re-enter password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground transition-all"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary/50"
                />
                <span className="text-xs text-muted-foreground font-medium">Remember me on this device</span>
              </label>
            </div>

            <Button
              type="submit"
              variant="default"
              className="w-full py-2.5 font-semibold text-sm mt-2"
              loading={loading}
            >
              Complete Registration & Sign In
            </Button>
          </form>
        )}

        {/* VIEW 4: OTP Verification Screen (Used for Register Step 2, OTP Login Step 2, Forgot Password Step 2) */}
        {(mode === 'verify_registration_otp' || mode === 'verify_login_otp' || mode === 'verify_reset_otp') && (
          <form 
            onSubmit={
              mode === 'verify_registration_otp' 
                ? handleVerifyRegistrationOtp 
                : mode === 'verify_login_otp' 
                ? handleVerifyLoginOtp 
                : handleResetPasswordSubmit
            } 
            className="space-y-4"
          >
            <div>
              <label className="block text-center text-xs font-semibold text-foreground mb-3 uppercase tracking-wider">
                Enter 6-Digit Code
              </label>
              <div className="flex justify-center gap-2 sm:gap-3">
                {otpDigits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleOtpDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-11 h-13 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold rounded-xl border-2 border-border focus:border-primary focus:ring-2 focus:ring-primary/20 bg-background text-foreground transition-all"
                  />
                ))}
              </div>
            </div>

            {mode === 'verify_reset_otp' && (
              <div className="space-y-3 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    New Password <span className="text-error">*</span>
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    required
                    placeholder="Min 6 characters"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2 rounded-xl border border-border bg-background text-sm text-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    Confirm New Password <span className="text-error">*</span>
                  </label>
                  <input
                    type="password"
                    name="confirmNewPassword"
                    required
                    placeholder="Confirm new password"
                    value={formData.confirmNewPassword}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2 rounded-xl border border-border bg-background text-sm text-foreground"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              className="w-full py-2.5 font-semibold text-sm mt-3"
              loading={loading}
            >
              {mode === 'verify_registration_otp' && 'Verify Code & Proceed'}
              {mode === 'verify_login_otp' && 'Verify & Sign In'}
              {mode === 'verify_reset_otp' && 'Set Password & Sign In'}
            </Button>

            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
              <span>Didn't receive code?</span>
              {resendCooldown > 0 ? (
                <span className="text-primary font-medium">
                  Resend in {resendCooldown}s
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading}
                  className="text-primary font-semibold hover:underline"
                >
                  Resend Code
                </button>
              )}
            </div>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <Icon name="ArrowLeft" size={14} /> Back to Sign In
              </button>
            </div>
          </form>
        )}

        {/* VIEW 4: Sign In with OTP */}
        {mode === 'otp_login' && (
          <form onSubmit={handleRequestOtpLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Registered Email Address <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground transition-all"
              />
            </div>

            <Button
              type="submit"
              variant="default"
              className="w-full py-2.5 font-semibold text-sm"
              loading={loading}
            >
              Send Login OTP
            </Button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <Icon name="ArrowLeft" size={14} /> Back to Password Sign In
              </button>
            </div>
          </form>
        )}

        {/* VIEW 5: Forgot Password */}
        {mode === 'forgot_password' && (
          <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Your Email Address <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground transition-all"
              />
            </div>

            <Button
              type="submit"
              variant="default"
              className="w-full py-2.5 font-semibold text-sm"
              loading={loading}
            >
              Send Reset Code
            </Button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <Icon name="ArrowLeft" size={14} /> Back to Sign In
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CandidateAuthModal;
